import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * ScreeningBar - Display screening hit summary with severity indicator
 *
 * Hyperkit Specs:
 * - Severity indicator: 8px dot with semantic color
 * - Label: 14px, flex-1
 * - Count: 18px font-weight-600
 * - Border bottom except last child
 */

const severityVariants = cva(
  'h-2 w-2 rounded-full shrink-0',
  {
    variants: {
      severity: {
        high: 'bg-red-500',
        medium: 'bg-orange-500',
        low: 'bg-blue-500',
        none: 'bg-grey-400 dark:bg-grey-500',
      },
    },
    defaultVariants: {
      severity: 'none',
    },
  }
);

const ScreeningBar = React.forwardRef(({
  className,
  severity,
  label,
  count,
  onClick,
  ...props
}, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex items-center py-3 border-b border-grey-200 dark:border-grey-700 last:border-b-0',
      onClick && 'cursor-pointer hover:bg-grey-50 dark:hover:bg-grey-800/50 -mx-2 px-2 rounded transition-colors',
      className
    )}
    onClick={onClick}
    {...props}
  >
    <span className={cn(severityVariants({ severity }), 'mr-3')} />
    <span className="flex-1 text-body-sm text-grey-700 dark:text-grey-300">
      {label}
    </span>
    <span className="text-heading-md text-grey-900 dark:text-grey-50">
      {count}
    </span>
  </div>
));
ScreeningBar.displayName = 'ScreeningBar';

/**
 * ScreeningBarSkeleton - Loading state
 */
const ScreeningBarSkeleton = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex items-center py-3 border-b border-grey-200 dark:border-grey-700 last:border-b-0 animate-pulse',
      className
    )}
    {...props}
  >
    <div className="h-2 w-2 rounded-full bg-grey-200 dark:bg-grey-700 mr-3" />
    <div className="flex-1 h-4 rounded bg-grey-200 dark:bg-grey-700" />
    <div className="h-5 w-8 rounded bg-grey-200 dark:bg-grey-700 ml-3" />
  </div>
));
ScreeningBarSkeleton.displayName = 'ScreeningBarSkeleton';

export { ScreeningBar, ScreeningBarSkeleton, severityVariants };
